import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { areasQuery } from '@/api/queries'
import { FilterBar } from '@/components/FilterBar'
import { EmptyState, PageHeader } from '@/components/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  REVIEW_FLAGS,
  REVIEW_KINDS,
  reviewQueueQuery,
  type ReviewFilters,
} from '@/features/review/api'
import { ReviewCard } from '@/features/review/ReviewCard'
import { requireNav } from '@/nav/guard'

const text = z.union([z.string(), z.number()]).transform(String).optional().catch(undefined)

export const Route = createFileRoute('/_app/review')({
  beforeLoad: requireNav('review'),
  validateSearch: z.object({
    kind: z
      .enum(REVIEW_KINDS as [string, ...string[]])
      .optional()
      .catch(undefined),
    flag: z
      .enum(REVIEW_FLAGS as [string, ...string[]])
      .optional()
      .catch(undefined),
    area: text,
  }),
  component: ReviewPage,
})

/** Prüfwarteschlange (GU/Bauleitung): Besuche, Fotos, Aufmaße; Lesemodus mit Aktionsleiste und Bestätigungsdialog. */
function ReviewPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [notice, setNotice] = useState<string | null>(null)
  const areas = useQuery(areasQuery('rajon'))

  const update = useCallback(
    (patch: Partial<typeof search>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  )
  const filters: ReviewFilters = {
    kind: search.kind as ReviewFilters['kind'],
    flag: search.flag as ReviewFilters['flag'],
    rajon: search.area,
  }
  const query = useInfiniteQuery(reviewQueueQuery(filters))
  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data])
  const total = query.data?.pages[0]?.total ?? 0
  const all = { value: '', label: t('common.all') }
  const areaKeys = [
    ...new Set([...(areas.data ?? []).map((a) => a.key).filter(Boolean), search.area ?? '']),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))

  return (
    <>
      <PageHeader title={t('pages.review.title')} description={t('pages.review.description')} />
      <FilterBar
        filters={[
          {
            id: 'kind',
            kind: 'select',
            label: t('review.filterKind'),
            value: search.kind ?? '',
            options: [
              all,
              ...REVIEW_KINDS.map((k) => ({ value: k, label: t(`review.kind.${k}`) })),
            ],
            onChange: (v) => update({ kind: v || undefined }),
          },
          {
            id: 'flag',
            kind: 'select',
            label: t('review.filterFlag'),
            value: search.flag ?? '',
            options: [
              all,
              ...REVIEW_FLAGS.map((f) => ({ value: f, label: t(`review.flag.${f}`) })),
            ],
            onChange: (v) => update({ flag: v || undefined }),
          },
          {
            id: 'area',
            kind: 'select',
            label: t('review.filterArea'),
            value: search.area ?? '',
            options: [all, ...areaKeys.map((k) => ({ value: k, label: k }))],
            onChange: (v) => update({ area: v || undefined }),
          },
        ]}
      />

      {notice && (
        <p role="status" className="mb-4 rounded-md border bg-card p-3">
          {notice}
        </p>
      )}

      {query.isPending ? (
        <Skeleton className="h-32 w-full" aria-label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState title={t('review.loadError')} body={t('errors.network')}>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        </EmptyState>
      ) : items.length === 0 ? (
        <EmptyState title={t('review.emptyTitle')} body={t('review.emptyBody')} />
      ) : (
        <>
          <p className="mb-3 font-medium" role="status" data-testid="review-count">
            {t('review.openCount', { count: total })}
          </p>
          <ul className="grid gap-4" aria-label={t('review.listLabel')}>
            {items.map((item) => (
              <ReviewCard key={`${item.kind}-${item.id}`} item={item} onDecided={setNotice} />
            ))}
          </ul>
          {query.hasNextPage && (
            <div className="mt-4">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? t('common.loading') : t('orders.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}
